import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Search, FileText, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/stores/appStore';
import { TopNav } from '@/components/layout/TopNav';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { ApiError, scrapeCreatorsApi, type ScrapeCreatorsPlatform } from '@/lib/api';
import { PLATFORMS } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';

const SC_PLATFORMS: ScrapeCreatorsPlatform[] = ['tiktok', 'instagram', 'youtube', 'x'];

type Lookup = 'profile' | 'posts';

export function CreatorResearch() {
  const { getToken } = useAuth();
  // Lookups are billed per workspace, so they need one selected.
  const { currentWorkspace } = useAppStore();
  const workspaceId = currentWorkspace?.id;

  // Handle-based lookups (profile / posts)
  const [platform, setPlatform] = useState<ScrapeCreatorsPlatform>('tiktok');
  const [handle, setHandle] = useState('');
  const [lookup, setLookup] = useState<Lookup>('profile');

  // URL-based transcript lookup
  const [tPlatform, setTPlatform] = useState<ScrapeCreatorsPlatform>('youtube');
  const [url, setUrl] = useState('');

  const handleSearch = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const h = handle.trim().replace(/^@/, '');
      return lookup === 'profile'
        ? scrapeCreatorsApi.profile(token!, workspaceId!, platform, h)
        : scrapeCreatorsApi.posts(token!, workspaceId!, platform, h);
    },
  });

  const transcript = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return scrapeCreatorsApi.transcript(token!, workspaceId!, tPlatform, url.trim());
    },
  });

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      <TopNav title="Creator Research" />

      <div className="p-6 space-y-6 max-w-4xl w-full">
        {/* Profile / Posts lookup */}
        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-accent" />
            <h2 className="text-sm font-semibold text-foreground">Profile & Posts</h2>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <PlatformIcon
                platform={platform}
                size="xs"
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"
              />
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as ScrapeCreatorsPlatform)}
                className="bg-input border border-border rounded-md pl-8 pr-2 py-1.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
              >
                {SC_PLATFORMS.map((p) => (
                  <option key={p} value={p}>
                    {PLATFORMS[p].label}
                  </option>
                ))}
              </select>
            </div>

            <SegmentedControl
              aria-label="Lookup type"
              options={(['profile', 'posts'] as Lookup[]).map((l) => ({ label: l, value: l }))}
              value={lookup}
              onChange={setLookup}
              className="capitalize"
            />

            <input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && handle.trim() && workspaceId) handleSearch.mutate();
              }}
              placeholder="handle, e.g. stoolpresidente"
              className="flex-1 min-w-[180px] bg-input border border-border rounded-md px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
            />

            <button
              onClick={() => handleSearch.mutate()}
              disabled={handleSearch.isPending || !handle.trim() || !workspaceId}
              className="flex items-center gap-1.5 bg-accent hover:bg-accent/90 text-accent-foreground px-4 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
            >
              {handleSearch.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </button>
          </div>

          <ResultPanel
            isPending={handleSearch.isPending}
            error={handleSearch.error}
            data={handleSearch.data?.data}
            emptyHint="Enter a handle and search to see profile or recent posts."
          />
        </div>

        {/* Transcript lookup */}
        <div className="bg-card border border-border rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-accent" />
            <h2 className="text-sm font-semibold text-foreground">Video Transcript</h2>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <PlatformIcon
                platform={tPlatform}
                size="xs"
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"
              />
              <select
                value={tPlatform}
                onChange={(e) => setTPlatform(e.target.value as ScrapeCreatorsPlatform)}
                className="bg-input border border-border rounded-md pl-8 pr-2 py-1.5 text-sm text-foreground outline-none focus:ring-1 focus:ring-accent"
              >
                {SC_PLATFORMS.map((p) => (
                  <option key={p} value={p}>
                    {PLATFORMS[p].label}
                  </option>
                ))}
              </select>
            </div>

            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && url.trim() && workspaceId) transcript.mutate();
              }}
              placeholder="post or video URL"
              className="flex-1 min-w-[220px] bg-input border border-border rounded-md px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-accent"
            />

            <button
              onClick={() => transcript.mutate()}
              disabled={transcript.isPending || !url.trim() || !workspaceId}
              className="flex items-center gap-1.5 bg-accent hover:bg-accent/90 text-accent-foreground px-4 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
            >
              {transcript.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              Get transcript
            </button>
          </div>

          <ResultPanel
            isPending={transcript.isPending}
            error={transcript.error}
            data={transcript.data?.data}
            emptyHint="Paste a video URL to fetch its transcript."
          />
        </div>
      </div>
    </div>
  );
}

function ResultPanel({
  isPending,
  error,
  data,
  emptyHint,
}: {
  isPending: boolean;
  error: Error | null;
  data: unknown;
  emptyHint: string;
}) {
  if (isPending) {
    return <div className="h-24 bg-muted rounded-md animate-pulse" />;
  }
  if (error) {
    const specific =
      error instanceof ApiError && error.status === 429
        ? error.message
        : error instanceof ApiError && error.status === 403
          ? 'Lookups need editor access to the current workspace.'
          : null;
    return (
      <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md p-3">
        <AlertCircle className="h-4 w-4 shrink-0" />
        {specific ??
          'Request failed. The handle/URL may be invalid or the ScrapeCreators API key is not set.'}
      </div>
    );
  }
  if (data === undefined) {
    return <p className="text-xs text-muted-foreground">{emptyHint}</p>;
  }
  return (
    <pre className="max-h-96 overflow-auto bg-muted/50 border border-border rounded-md p-3 text-[11px] leading-relaxed text-foreground whitespace-pre-wrap break-words">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
